import "./App.css";
import { persistStore } from "redux-persist";
import { store } from "./store/store";
import { Provider } from "react-redux";
import { useEffect, useState } from "react";
import {
  closeAnnotationDialog,
  closeExtractDialog,
  closeClipDetailsDialog,
} from "./store/dialogs";
import { reset as caseReset } from "./store/case";
import { reset as clipReset } from "./store/clip";
import { reset as editorReset } from "./store/editor";
import { reset as glimpseReset } from "./store/glimpse";
import { reset as licenseReset } from "./store/license";
import { reset as userReset } from "./store/user";
import { reset as exportCommitReset } from "./store/exportCommit";
import { reset as usersReset } from "./store/users";
import { reset as noiseFilterReset } from "./store/noiseFilter";
import { reset as uploadQueueReset } from "./store/uploadQueue";
import { loadVersion } from "./store/version";
import { reset as filteredFilesReset } from "./store/filteredFiles";
import { PersistGate } from "redux-persist/integration/react";
import AppLoading from "./components/AppLoading";
import { BrowserRouter } from "react-router-dom";
import AppRoutes from "./routes/AppRoutes";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { appTheme } from "./AppTheme";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "./store/config";
import { FlagsProvider } from "flagged";
import { setFeatures, NON_REACT_FEATURE_FLAGS } from "./FeatureFlags";

console.log("[App] Configuring Persistor");
const persistor = persistStore(store);
console.log("[App] Done Configuring Store");

export const fullCaseReset = async (dispatch) => {
  await dispatch(loadVersion());
  await dispatch(filteredFilesReset());
  await dispatch(editorReset());
  await dispatch(glimpseReset());
  await dispatch(caseReset());
  await dispatch(clipReset());
  await dispatch(uploadQueueReset(0)); //No case at this point. It just clears the queue
};

function App() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const start = async () => {
      await store.dispatch(licenseReset());
      await store.dispatch(userReset());
      await store.dispatch(usersReset());
      //store.dispatch(loadUsers()); //TODO: Only call for admins
      // await store.dispatch(appStartTimerPumpInterval());
      await store.dispatch(exportCommitReset());
      await store.dispatch(noiseFilterReset());
      await store.dispatch(closeClipDetailsDialog());
      await store.dispatch(closeAnnotationDialog());
      await store.dispatch(closeExtractDialog());
      await fullCaseReset(store.dispatch);
      setLoaded(true);
    };
    start();
  }, []);

  const [websocketEnabled, setWebsocketEnabled] = useState(false);

  /**
   * Setup a global websocket
   */
  const { readyState } = useWebSocket(WEBSOCKET_URL, {
    onOpen: () => {
      console.log(">>>>>WebSocket connection established.", WEBSOCKET_URL);
    },
    //      heartbeat: true
    heartbeat: {
      message: "ping",
      returnMessage: "pong",
      timeout: 60000 * 5,
      interval: 10000,
    },
  });

  useEffect(() => {
    console.log("READY STATE: " + readyState);
    if (readyState === 1) {
      setWebsocketEnabled(true);
    } else if (readyState === 0) {
      setWebsocketEnabled(false);
    }
  }, [readyState]);

  //The readystate check is to prevent the app from re-rendering everything every time the websocket state changes.
  if (websocketEnabled && loaded) {
    return (
      <Provider store={store}>
        <PersistGate loading={<AppLoading />} persistor={persistor}>
          <FlagsProvider
            features={{ ...setFeatures(), ...NON_REACT_FEATURE_FLAGS }}
          >
            <BrowserRouter>
              <ThemeProvider theme={appTheme}>
                <CssBaseline />
                <AppRoutes />
              </ThemeProvider>
            </BrowserRouter>
          </FlagsProvider>
        </PersistGate>
      </Provider>
    );
  } else {
    return <AppLoading />;
  }
}

export default App;
